import { useEffect } from "react";
import { SITE_URL } from "./config";
import type { SeoMeta } from "./config";

function ensureMetaTag(selector: string, key: string, keyName: "name" | "property"): HTMLMetaElement {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(keyName, key);
    document.head.appendChild(el);
  }
  return el;
}

function setMeta(key: string, content: string, keyName: "name" | "property" = "name") {
  const selector = `meta[${keyName}="${key}"]`;
  const el = ensureMetaTag(selector, key, keyName);
  el.setAttribute("content", content);
}

function removeMeta(key: string, keyName: "name" | "property" = "name") {
  const el = document.head.querySelector<HTMLMetaElement>(`meta[${keyName}="${key}"]`);
  if (el) {
    el.remove();
  }
}

function setCanonical(href: string) {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

/**
 * 路由感知的 head 管理 hook：每次路由 / 语言变化时，
 * 统一更新 title、description、keywords、canonical、robots 以及 OG / Twitter 标签，
 * 避免上一个路由的元数据泄漏到下一个路由。
 */
export function useSeoHead(meta: SeoMeta | null, language: string) {
  useEffect(() => {
    if (!meta) return;

    document.title = meta.title;

    setMeta("description", meta.description);
    setMeta("keywords", meta.keywords);

    setCanonical(`${SITE_URL}${meta.path === "/" ? "/" : meta.path}`);

    if (meta.noindex) {
      setMeta("robots", "noindex, nofollow");
    } else {
      removeMeta("robots");
    }

    const ogTitle = meta.ogTitle ?? meta.title;
    const ogDescription = meta.ogDescription ?? meta.description;

    setMeta("og:title", ogTitle, "property");
    setMeta("og:description", ogDescription, "property");
    setMeta("og:url", `${SITE_URL}${meta.path === "/" ? "/" : meta.path}`, "property");
    setMeta("og:type", meta.type ?? "website", "property");
    setMeta("og:locale", language || "en", "property");

    setMeta("twitter:title", ogTitle);
    setMeta("twitter:description", ogDescription);

    if (meta.image) {
      setMeta("og:image", meta.image, "property");
      setMeta("twitter:image", meta.image);
    } else {
      removeMeta("og:image", "property");
      removeMeta("twitter:image");
    }
  }, [meta, language]);
}